/**
 * MaterialHero Component
 * ======================
 * DARK PREMIUM THEME
 * Hero section - material as dominant visual
 * Immersive, cinematic, minimal UI
 */

import React from 'react';
import { useBlueprint } from '../../context/BlueprintContext';
import { ArrowLeft, Share2, Sparkles, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SaveButton from '../workspace/SaveButton';
import AddToProjectButton from '../workspace/AddToProjectButton';
import { getMaterialImageUrl } from '../../utils/materialImages';

const MaterialHero = ({ 
  material, 
  onSave, 
  onShare,
  onOpenSRE 
}) => {
  const navigate = useNavigate();
  const { getButton } = useBlueprint();
  
  if (!material) return null;
  
  const {
    id,
    name,
    category,
    color_family,
    origin
  } = material;
  
  // UNICO STANDARD: image_main via getMaterialImageUrl
  const displayImage = getMaterialImageUrl(material);
  
  // Material data for saving
  const materialData = {
    name,
    image: displayImage,
    type: category
  };
  
  return (
    <section className="relative bg-[#0A0A09]" data-testid="material-hero">
      {/* Full-screen Material Image */}
      <div className="relative h-[75vh] min-h-[600px] overflow-hidden">
        <img
          src={displayImage}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-material.svg'; }}
        />
        
        {/* Cinematic Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A09] via-[#0A0A09]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A09]/60 to-transparent" />
        
        {/* Top Navigation - Minimal */}
        <div className="absolute top-0 left-0 right-0 p-6 z-20">
          <div className="max-w-[1400px] mx-auto px-4 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium tracking-wide">
                {getButton('back', 'Indietro')}
              </span>
            </button>
            
            <div className="flex items-center gap-2">
              {/* Add to Project */}
              <AddToProjectButton
                materialId={id}
                materialData={materialData}
                variant="icon"
                size="md"
                className="!bg-white/5 !backdrop-blur-md border border-white/10 hover:!bg-white/10"
              />
              
              {/* Share */}
              <button
                onClick={onShare}
                className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
                title={getButton('share', 'Condividi')}
              >
                <Share2 size={16} />
              </button>
              
              {/* Save Material */}
              <SaveButton
                materialId={id}
                materialData={materialData}
                variant="icon"
                size="md"
                className="!bg-white/5 !backdrop-blur-md border border-white/10 hover:!bg-white/10"
              />
            </div>
          </div>
        </div>
        
        {/* Content - Bottom Left */}
        <div className="absolute bottom-0 left-0 right-0 pb-12 pt-32 z-10">
          <div className="max-w-[1400px] mx-auto px-8">
            {/* Category & Origin - Subtle */}
            <div className="flex items-center gap-4 mb-5">
              <span className="text-[10px] font-bold tracking-[3px] uppercase text-[#D6C1A5]">
                {category || 'Material'}
              </span>
              {origin && (
                <>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="text-[10px] tracking-[2px] uppercase text-white/50">
                    {origin}
                  </span>
                </>
              )}
            </div>
            
            {/* Material Name - Large, Editorial */}
            <h1 className="font-[var(--ff-h)] text-[clamp(48px,7vw,96px)] font-light text-white leading-[0.95] tracking-[-0.02em] mb-8 max-w-4xl">
              {name}
            </h1>
            
            {/* Color Family Badge - Elegant */}
            {color_family && (
              <div className="flex items-center gap-3 mb-8">
                <div className="w-3 h-3 rounded-full bg-[#D6C1A5]/80" />
                <span className="text-sm text-white/60 capitalize tracking-wide">
                  {color_family}
                </span>
              </div>
            )}
            
            {/* Actions - Premium */}
            <div className="flex items-center gap-4">
              <button
                onClick={onOpenSRE}
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#D6C1A5] text-[#0A0A09] font-semibold text-sm tracking-wide rounded-none hover:bg-[#E0CBB5] transition-all"
              >
                <Eye size={18} />
                {getButton('launch_sre', 'Apri in SRE')}
                <Sparkles size={14} className="opacity-60 group-hover:opacity-100 transition-opacity" />
              </button>
              
              {/* Save Button - Full variant */}
              <SaveButton
                materialId={id}
                materialData={materialData}
                variant="button"
                size="lg"
                className="!px-7 !py-4 !rounded-none !border-white/20 hover:!border-white/40"
              />
            </div>
          </div>
        </div>
        
        {/* SRE Badge - Top Right */}
        <div className="absolute top-24 right-8 z-20">
          <div className="bg-[#0A0A09]/80 backdrop-blur-xl border border-white/10 px-4 py-3 flex items-center gap-3">
            <div>
              <div className="text-[8px] font-bold tracking-[2px] text-[#D6C1A5] mb-0.5">
                SRE ENGINE
              </div>
              <div className="text-[11px] text-white/50">
                Real Slab Preview
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-[#D6C1A5]/20 flex items-center justify-center">
              <Eye size={14} className="text-[#D6C1A5]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MaterialHero;
